<template>
    <v-container grid-list-md pa-0 relative>
        <v-layout row wrap>
            <v-flex xs12>
                <v-card outlined class="primary--border">
                    <v-card-title class="title">
                        Materialized View Report

                        <v-spacer />

                        <print-buttons @action="download" v-if="records.length" :passAuthToReport="true" :pdf="false"
                            :excel="true" labelExcel="Download Excel" labelPdf="Download PDF" />
                    </v-card-title>

                    <v-card outlined>
                        <v-card-title class="title mt-3">

                            <v-flex xs12 sm3 md2>
                                <v-calendar-field id-val="from_date" v-model="frmDate" label="From Date" />
                            </v-flex>

                            <v-flex xs12 sm3 md2>
                                <v-calendar-field id-val="to_date" v-model="toDate" label="To Date" />
                            </v-flex>

                            <v-flex xs12 sm3 md2>
                                <v-text-field v-model="billNo" label="Bill Number" outlined dense clearable hide-details
                                    @keyup.enter="search" />
                            </v-flex>


                            <v-flex xs12 sm2 md1>
                                <v-select v-model="syncStatus" :items="syncStatusItems" label="IRD Sync" outlined dense
                                    hide-details />
                            </v-flex>

                            <v-flex xs12 sm2 md2>
                                <v-select v-model="cancelStatus" :items="cancelStatusItems" label="Bill Status" outlined
                                    dense hide-details />
                            </v-flex>

                            <v-flex xs12 sm2 md1>
                                <search-button :disabled="loading" permission="receipt-read" @action="search">
                                    Search
                                </search-button>
                            </v-flex>
                        </v-card-title>

                        <div v-if="records.length" class="data-represent" style="margin-top: -10px;">
                            <div>
                                <span class="d-info"></span>
                                &nbsp;

                                <small>
                                    Showing
                                    <strong>{{ records.length }}</strong>
                                    of
                                    <strong>{{ totalItems }}</strong>
                                    bill(s)
                                </small>
                            </div>

                            <div>
                                <span class="d-error"></span>
                                &nbsp;

                                <small>
                                    Page Taxable Amount:
                                    <strong>{{ currency(totalTaxableAmount) }}</strong>
                                </small>
                            </div>

                            <div>
                                <span class="d-warning"></span>
                                &nbsp;

                                <small>
                                    Page VAT:
                                    <strong>{{ currency(totalTaxAmount) }}</strong>
                                </small>
                            </div>

                            <div>
                                <span class="d-success"></span>
                                &nbsp;

                                <small>
                                    Page Grand Total:
                                    <strong>{{ currency(grandTotal) }}</strong>
                                </small>
                            </div>
                        </div>

                        <br />
                    </v-card>

                    <v-data-table :headers="headers" :items="records" :options.sync="pagination" :footer-props="{
                        ...footerProps,
                        itemsPerPageOptions: [10, 25, 50, 100]
                    }" :server-items-length="totalItems" :loading="loading" item-key="field" class="ird-bill-table">
                        <template v-slot:item="{ index, item }">
                            <tr :class="{ 'cancelled-row': isCancelled(item) }">
                                <td class="text-center">
                                    {{ rowNumber(index) }}
                                </td>

                                <td class="text-left">
                                    <strong>{{ item.fiscal_year || "-" }}</strong>
                                </td>

                                <td class="text-left">
                                    <strong>{{ item.bill_no || "-" }}</strong>

                                    <small v-if="getTransactionId(item)" class="cell-subtitle">
                                        {{ getTransactionId(item) }}
                                    </small>
                                </td>

                                <td class="text-left">
                                    <strong>{{ item.customer_name || "-" }}</strong>

                                    <small class="cell-subtitle">
                                        PAN: {{ item.customer_pan || "-" }}
                                    </small>
                                </td>

                                <td class="text-left">
                                    {{ item.bill_date || "-" }}
                                </td>

                                <td class="text-right">
                                    {{ currency(item.amount) }}
                                </td>

                                <td class="text-right">
                                    {{ currency(item.discount) }}
                                </td>

                                <td class="text-right">
                                    {{ currency(item.taxable_amount) }}
                                </td>

                                <td class="text-right">
                                    {{ currency(item.tax_amount) }}
                                </td>

                                <td class="text-right">
                                    <strong>{{ currency(item.total_amount) }}</strong>
                                </td>

                                <td class="text-center">
                                    <v-chip x-small label :color="booleanColor(item.sync_with_ird)"
                                        text-color="white">
                                        {{ booleanLabel(item.sync_with_ird) }}
                                    </v-chip>
                                </td>

                                <td class="text-center">
                                    <v-chip x-small label :color="booleanColor(item.is_bill_printed)"
                                        text-color="white">
                                        {{ booleanLabel(item.is_bill_printed) }}
                                    </v-chip>
                                </td>

                                <td class="text-center">
                                    <v-chip x-small label :color="booleanColor(item.is_bill_active)" text-color="white">
                                        {{ booleanLabel(item.is_bill_active) }}
                                    </v-chip>
                                </td>

                                <td class="text-left">
                                    {{ item.printed_time || "-" }}
                                </td>

                                <td class="text-left">
                                    {{ item.entered_by ? item.entered_by.trim() : "-" }}
                                </td>

                                <td class="text-left">
                                    {{ item.printed_by || "-" }}
                                </td>

                                <td class="text-center">
                                    <v-chip x-small label :color="booleanColor(item.is_realtime)" text-color="white">
                                        {{ booleanLabel(item.is_realtime) }}
                                    </v-chip>
                                </td>

                                <td class="text-left">
                                    {{ item.payment_method || "-" }}
                                </td>

                                <td class="text-right">
                                    {{ currency(getVatRefundAmount(item)) }}
                                </td>

                                <td class="text-left">
                                    {{ getTransactionId(item) || "-" }}
                                </td>

                                <td class="text-center">
                                    <v-btn v-if="$auth.can('bill-generate-student-wise')" small icon color="primary"
                                        class="pa-0 ma-1" title="Print Invoice" @click="printInvoice(item)">
                                        <v-icon dark>print</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </template>

                        <template v-slot:body.append>
                            <tr v-if="records.length" class="total-row">
                                <td colspan="5">
                                    <strong>Current page total</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ currency(totalAmount) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ currency(totalDiscount) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ currency(totalTaxableAmount) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ currency(totalTaxAmount) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ currency(grandTotal) }}</strong>
                                </td>

                                <td colspan="8"></td>

                                <td class="text-right">
                                    <strong>{{ currency(totalVatRefundAmount) }}</strong>
                                </td>

                                <td></td>

                                <td></td>
                            </tr>
                        </template>

                        <template v-slot:no-data>
                            <div class="pa-8 text-center grey--text">
                                No billing records found for the selected filters.
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import Mixins from "@/library/Mixins";
import config from "@/library/Config.js";
import {
    redirectDownloadUrl,
    reportServiceUrl,
    v2Services,
} from "@/library/helpers";

const NepaliDate = require("nepali-date-js");
const nd = new NepaliDate();

export default {
    name: "IrdSalesBillReport",

    mixins: [Mixins],

    data: () => ({
        records: [],
        loading: false,
        downloading: false,
        requestInitialized: false,

        batch: {},

        fiscalYearId: "all",

        frmDate:
            nd.getYear() +
            "-" +
            nd.getMonth().pad() +
            "-" +
            nd.getDate().pad(),

        toDate: nd.format("YYYY-MM-DD"),

        billNo: "",
        customer: "",
        syncStatus: "all",
        cancelStatus: "all",
        downloadUrl: "",

        totalItems: 0,

        totalAmount: 0,
        totalDiscount: 0,
        totalTaxableAmount: 0,
        totalTaxAmount: 0,
        grandTotal: 0,
        totalVatRefundAmount: 0,

        pagination: {
            page: 1,
            itemsPerPage: 50,
            sortBy: [],
            sortDesc: [],
        },

        syncStatusItems: [
            {
                text: "All",
                value: "all",
            },
            {
                text: "Synced",
                value: "synced",
            },
            {
                text: "Not Synced",
                value: "not_synced",
            },
        ],

        cancelStatusItems: [
            {
                text: "All",
                value: "all",
            },
            {
                text: "Cancelled",
                value: "cancelled",
            },
            {
                text: "Not Cancelled",
                value: "not_cancelled",
            },
        ],

        headers: [
            {
                text: "#",
                align: "center",
                value: "field",
                width: 60,
                sortable: false,
            },
            {
                text: "Fiscal Year",
                align: "left",
                value: "fiscal_year",
                width: 120,
                sortable: false,
            },
            {
                text: "Bill No.",
                align: "left",
                value: "bill_no",
                width: 180,
                sortable: false,
            },
            {
                text: "Customer",
                align: "left",
                value: "customer_name",
                width: 220,
                sortable: false,
            },
            {
                text: "Bill Date",
                align: "left",
                value: "bill_date",
                width: 130,
                sortable: false,
            },
            {
                text: "Amount",
                align: "right",
                value: "amount",
                width: 130,
                sortable: false,
            },
            {
                text: "Discount",
                align: "right",
                value: "discount",
                width: 120,
                sortable: false,
            },
            {
                text: "Taxable Amount",
                align: "right",
                value: "taxable_amount",
                width: 150,
                sortable: false,
            },
            {
                text: "Tax Amount",
                align: "right",
                value: "tax_amount",
                width: 130,
                sortable: false,
            },
            {
                text: "Total Amount",
                align: "right",
                value: "total_amount",
                width: 150,
                sortable: false,
            },
            {
                text: "IRD Sync",
                align: "center",
                value: "sync_with_ird",
                width: 110,
                sortable: false,
            },
            {
                text: "Printed",
                align: "center",
                value: "is_bill_printed",
                width: 100,
                sortable: false,
            },
            {
                text: "Active",
                align: "center",
                value: "is_bill_active",
                width: 100,
                sortable: false,
            },
            {
                text: "Printed Time",
                align: "left",
                value: "printed_time",
                width: 180,
                sortable: false,
            },
            {
                text: "Entered By",
                align: "left",
                value: "entered_by",
                width: 150,
                sortable: false,
            },
            {
                text: "Printed By",
                align: "left",
                value: "printed_by",
                width: 150,
                sortable: false,
            },
            {
                text: "Realtime",
                align: "center",
                value: "is_realtime",
                width: 100,
                sortable: false,
            },
            {
                text: "Payment Method",
                align: "left",
                value: "payment_method",
                width: 150,
                sortable: false,
            },
            {
                text: "VAT Refund",
                align: "right",
                value: "VAT_Refund_Amount",
                width: 140,
                sortable: false,
            },
            {
                text: "Transaction ID",
                align: "left",
                value: "Transaction Id",
                width: 190,
                sortable: false,
            },
            {
                text: "Action",
                align: "center",
                value: "action",
                width: 90,
                sortable: false,
            },
        ],
    }),

    mounted() {
        this.batch = JSON.parse(window.localStorage.getItem("batch")) || {};
    },

    watch: {
        pagination: {
            deep: true,
            handler(newValue, oldValue) {
                if (!this.requestInitialized) {
                    this.requestInitialized = true;
                    this.get();
                    return;
                }

                if (!oldValue) {
                    return;
                }

                const pageChanged = newValue.page !== oldValue.page;
                const perPageChanged =
                    newValue.itemsPerPage !== oldValue.itemsPerPage;

                if (pageChanged || perPageChanged) {
                    this.get();
                }
            },
        },
    },

    methods: {
        search() {
            if (this.pagination.page !== 1) {
                this.pagination.page = 1;
                return;
            }

            this.get();
        },

        buildFilterParams() {
            const params = new URLSearchParams();

            params.append("from", this.frmDate || "");
            params.append("to", this.toDate || "");
            params.append("billNo", this.billNo || "");
            params.append("syncStatus", this.syncStatus || "all");
            params.append("cancelStatus", this.cancelStatus || "all");

            return params;
        },

        download() {
            if (this.downloading) {
                return;
            }

            const params = this.buildFilterParams();

            this.downloading = true;

            this.$restV2(v2Services.BILLING)
                .get(`/v2/reports/ird-bill-schedule/download?${params.toString()}`, {
                    responseType: "blob",
                })
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `ird-bill-schedule-${this.frmDate}-to-${this.toDate}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                })
                .finally(() => {
                    this.downloading = false;
                });
        },

        get() {
            const params = this.buildFilterParams();

            // params.append(
            //     "fiscalYearId",
            //     this.fiscalYearId || "all"
            // );
            // params.append("customer", this.customer || "");
            params.append("page", this.pagination.page || 1);
            params.append(
                "perPage",
                this.pagination.itemsPerPage || 50
            );

            this.loading = true;
            this.records = [];
            this.totalItems = 0;
            this.downloadUrl = "";
            this.resetTotals();

            this.$restV2(v2Services.BILLING)
                .get(`/v2/reports/ird-bill-schedule?${params.toString()}`)
                .then((response) => {
                    const payload = response.data || {};
                    const records = Array.isArray(payload.data)
                        ? payload.data
                        : [];

                    this.records = records;
                    this.totalItems = payload.meta
                        ? Number(payload.meta.total || 0)
                        : records.length;

                    this.downloadUrl =
                        payload.download_url ||
                        payload.downloadUrl ||
                        "";

                    this.calculateTotals(records);
                })
                .catch(() => {
                    this.records = [];
                    this.totalItems = 0;
                    this.resetTotals();
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        calculateTotals(records) {
            this.resetTotals();

            records.forEach((item) => {
                this.totalAmount += this.toNumber(item.amount);
                this.totalDiscount += this.toNumber(item.discount);
                this.totalTaxableAmount += this.toNumber(
                    item.taxable_amount
                );
                this.totalTaxAmount += this.toNumber(item.tax_amount);
                this.grandTotal += this.toNumber(item.total_amount);
                this.totalVatRefundAmount += this.toNumber(
                    this.getVatRefundAmount(item)
                );
            });
        },

        resetTotals() {
            this.totalAmount = 0;
            this.totalDiscount = 0;
            this.totalTaxableAmount = 0;
            this.totalTaxAmount = 0;
            this.grandTotal = 0;
            this.totalVatRefundAmount = 0;
        },

        getVatRefundAmount(item) {
            return (
                item.VAT_Refund_Amount ??
                item["VAT Refund Amount"] ??
                item["VAT_Refund_Amount (if any)"] ??
                0
            );
        },

        getTransactionId(item) {
            return (
                item.Transaction_Id ||
                item.Transaction_id ||
                item["Transaction Id"] ||
                item["Transaction ID"] ||
                item["Transaction Id (if any)"] ||
                null
            );
        },

        getEnrollId(item) {
            return item.enroll_id || null;
        },

        getEnrollCode(item) {
            return item.enroll_code || null;
        },

        /**
         * bill_month is the numeric ordinal (1-12) the invoice was billed
         * for, straight from the invoices table. The year still has to come
         * from the current batch (or the bill date as a last resort) since
         * bill_month alone doesn't carry one.
         */
        getBillingMonth(item) {
            const month = Number(item.bill_month);

            if (!month || month < 1 || month > 12) {
                return null;
            }

            const year =
                (this.batch.start && this.batch.start.substring(0, 4)) ||
                (item.bill_date && String(item.bill_date).split("-")[0]) ||
                "";

            if (!year) {
                return null;
            }

            return { year, month: String(month).padStart(2, "0") };
        },

        printInvoice(item) {
            const enrollId = this.getEnrollId(item);
            const enrollCode = this.getEnrollCode(item);

            if (!enrollId || !enrollCode) {
                this.$events.fire("notification", {
                    message:
                        "This bill cannot be printed: student reference is missing.",
                    status: "error",
                });
                return;
            }

            // The single bill print looks the invoice up by enroll alone, so a
            // missing month is not worth refusing the print over.
            const billingMonth = this.getBillingMonth(item) || {};

            const currentYear = this.batch.start
                ? this.batch.start.substring(0, 4)
                : billingMonth.year || "";

            const url =
                `billing=${this.billingOrdinal(billingMonth.month)}` +
                `&billMonth=${billingMonth.month || ""}` +
                `&enrollCode=${encodeURIComponent(enrollCode)}` +
                `&enroll=${enrollId}` +
                `&billDate=${item.bill_date || ""}` +
                `&inv_sequence=${item.inv_sequence || ""}` +
                `&onlyDue=false` +
                `&printed_by=${encodeURIComponent(this.$auth.user().full_name)}` +
                `&currentYear=${currentYear}` +
                (config.isV2Mode ? `&cbms=true` : ``);

            redirectDownloadUrl({
                uri: "/print/bill/single",
                host: reportServiceUrl(),
                queryString: url,
            });
        },

        billingOrdinal(month) {
            const ordinals = [
                "1st",
                "2nd",
                "3rd",
                "4th",
                "5th",
                "6th",
                "7th",
                "8th",
                "9th",
                "10th",
                "11th",
                "12th",
            ];

            return ordinals[Number(month) - 1] || "";
        },

        toNumber(value) {
            const number = parseFloat(value);

            return Number.isNaN(number) ? 0 : number;
        },

        currency(value) {
            return new Intl.NumberFormat("en-NP", {
                style: "currency",
                currency: "NPR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(this.toNumber(value));
        },

        booleanLabel(value) {
            return value ? "Yes" : "No";
        },

        booleanColor(value) {
            return value ? "success" : "grey";
        },

        isCancelled(item) {
            return !item.is_bill_active;
        },

        rowNumber(index) {
            const page = this.pagination.page || 1;
            const perPage = this.pagination.itemsPerPage || 50;
            return (page - 1) * perPage + index + 1;
        },
    },
};
</script>

<style lang="scss" scoped>
.ird-bill-table {
    width: 100%;
}

.ird-bill-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}

.ird-bill-table ::v-deep table {
    min-width: 2890px;
}

.ird-bill-table ::v-deep th {
    white-space: nowrap;
    font-weight: 700;
}

.ird-bill-table ::v-deep td {
    white-space: nowrap;
}

/**
 * Everything below lives inside a scoped slot, so it is rendered by the data
 * table and never picks up this component's scope id. The rules have to be
 * deep for that reason.
 */
.ird-bill-table ::v-deep .cell-subtitle {
    display: block;
    margin-top: 2px;
    color: #888;
    font-weight: 400;
}

.ird-bill-table ::v-deep .cancelled-row {
    opacity: 0.6;
}

.ird-bill-table ::v-deep .cancelled-row td:first-child {
    border-left: 3px solid #ff5252 !important;
}

.ird-bill-table ::v-deep .total-row {
    background-color: #fafafa;
}

.ird-bill-table ::v-deep .total-row td {
    border-top: 2px solid #ddd !important;
}
</style>